import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { GraduationCap, Briefcase, Loader2, Mail } from "lucide-react";
import { COMPANY } from "@/components/laneeka/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITIONS = [
  "Vacancy — Industrial Automation Specialist",
  "Internship — HR",
  "Internship — Marketing",
  "Internship — IT",
  "Internship — Finance",
  "Internship — Others",
];

export default function CareerPage() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", position: POSITIONS[0], message: "" });
  const [cvFile, setCvFile] = useState(null);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickFile = (e) => {
    const f = e.target.files[0];
    if (f && f.size > 5 * 1024 * 1024) {
      toast.error("CV must be under 5MB.");
      e.target.value = "";
      return;
    }
    setCvFile(f || null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please provide your name and phone number.");
      return;
    }
    setSending(true);
    try {
      let resumePath = null;
      if (cvFile) {
        const fd = new FormData();
        fd.append("file", cvFile);
        const up = await axios.post(`${API}/upload?kind=resume`, fd);
        resumePath = up.data.path;
      }
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        product: form.position,
        message: form.message.trim() || null,
        type: "career",
        resume_path: resumePath,
      });
      toast.success("Application received. Our HR team will contact you if shortlisted.");
      setForm({ name: "", phone: "", email: "", position: POSITIONS[0], message: "" });
      setCvFile(null);
    } catch {
      toast.error("Could not submit right now — please email your resume instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-slate-300 rounded-md px-3 py-2.5 text-slate-900 focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700 transition-colors bg-white";

  return (
    <main data-testid="career-page" className="pt-[108px]">
      <section className="bg-slate-950 border-b border-slate-800 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-400 mb-3">Career</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">Join Our Team</h1>
          <p className="mt-3 text-slate-400 max-w-2xl">
            Grow your career with Laneeka International — a concern of the L&amp;P Industrial Automation family.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6 mb-12">
          <motion.div data-testid="vacancy-card" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="border border-slate-200 rounded-xl p-8">
            <Briefcase className="w-7 h-7 text-blue-700 mb-4" strokeWidth={1.75} />
            <h2 className="text-xl font-bold text-slate-900">Current Vacancy</h2>
            <p className="mt-3 font-semibold text-blue-700">Industrial Automation Specialist</p>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              Experience in belting systems and automation. Provide technical support, manage installations,
              and optimize automation solutions for our clients.
            </p>
          </motion.div>
          <motion.div data-testid="internship-card" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.08 }}
            className="border border-slate-200 rounded-xl p-8">
            <GraduationCap className="w-7 h-7 text-blue-700 mb-4" strokeWidth={1.75} />
            <h2 className="text-xl font-bold text-slate-900">Internship Program</h2>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">
              An exciting internship for students or recent graduates interested in industrial automation and
              belting systems. Gain hands-on experience, work with industry experts, and develop practical skills
              in a fast-paced, innovative environment.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {["HR", "Marketing", "IT", "Finance", "Others"].map((d) => (
                <span key={d} className="text-xs font-semibold bg-slate-100 text-slate-700 px-3 py-1.5 rounded-full">{d}</span>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-4">Apply now</h2>
            <p className="text-slate-600 leading-relaxed">
              Fill out the application form and our HR team will review your profile. You can also email your
              resume directly:
            </p>
            <a data-testid="career-email-link" href={`mailto:${COMPANY.email}`}
              className="mt-4 inline-flex items-center gap-2 text-blue-700 font-semibold hover:text-blue-800 transition-colors">
              <Mail className="w-4 h-4" /> {COMPANY.email}
            </a>
            <div className="mt-8 bg-slate-50 border border-slate-200 rounded-xl p-6 text-sm text-slate-600 leading-relaxed">
              <p className="font-semibold text-slate-900 mb-2">Office</p>
              <p>Matrikunja, 5 Dakshin Para Jame Masjid Road, Dakshin Khan, Dhaka-1230, Bangladesh</p>
              <p className="mt-2">{COMPANY.phone} · Sat – Thu, 9AM – 6PM</p>
            </div>
          </div>

          <form data-testid="career-application-form" onSubmit={submit} className="bg-slate-50 border border-slate-200 rounded-xl p-6 sm:p-8">
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-xs font-medium text-slate-500 mb-1.5">Full name *</span>
                <input data-testid="career-input-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-slate-500 mb-1.5">Phone *</span>
                <input data-testid="career-input-phone" required value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+8801…" />
              </label>
            </div>
            <label className="block mt-4">
              <span className="block text-xs font-medium text-slate-500 mb-1.5">Email</span>
              <input data-testid="career-input-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
            </label>
            <label className="block mt-4">
              <span className="block text-xs font-medium text-slate-500 mb-1.5">Position / Department</span>
              <select data-testid="career-select-position" value={form.position} onChange={set("position")} className={inputCls}>
                {POSITIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </label>
            <label className="block mt-4">
              <span className="block text-xs font-medium text-slate-500 mb-1.5">Educational background &amp; why you're interested</span>
              <textarea data-testid="career-input-message" rows={4} value={form.message} onChange={set("message")}
                className={`${inputCls} resize-none`} placeholder="Your education, experience, and motivation…" />
            </label>
            <label className="block mt-4">
              <span className="block text-xs font-medium text-slate-500 mb-1.5">Upload CV / Resume (PDF, DOC — max 5MB)</span>
              <input data-testid="career-input-resume" type="file" accept=".pdf,.doc,.docx" onChange={pickFile}
                className="w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-slate-900 file:text-white file:text-xs file:font-semibold hover:file:bg-blue-700 file:cursor-pointer" />
              {cvFile && <span className="block mt-1.5 text-xs text-blue-700 font-medium" data-testid="career-resume-name">{cvFile.name}</span>}
            </label>
            <button data-testid="career-submit-button" type="submit" disabled={sending}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white font-semibold py-3 rounded-md transition-colors">
              {sending && <Loader2 className="w-4 h-4 animate-spin" />}
              {sending ? "Submitting…" : "Submit Application"}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}
